

import { useState, useEffect } from "react";
import Modal from "../components/Modal";
import { formatCurrency, localToday, formatDateForInput } from "@/lib/data";
import { useToast } from "@/context/ToastContext";
import { apiClient } from "@/lib/api";
import { getImageUrl } from "@/lib/utils";
import ImageUpload from "@/components/ImageUpload";
import { Calendar, Tag, FileText, CreditCard } from "lucide-react";
import SearchableDropdown from "../components/SearchableDropdown";

interface ExpenseCategory {
  id: number;
  name: string;
}

interface Vendor {
  id: number;
  name: string;
}

interface Event {
  id: number;
  name: string;
}

interface Expense {
  id?: number;
  title: string;
  description?: string;
  amount: number;
  expense_date: string;
  expense_category_id?: number;
  vendor_id?: number;
  event_id?: number;
  payment_method?: string;
  reference_number?: string;
  notes?: string;
  proof_path?: string;
}

interface ExpenseModalProps {
  isOpen: boolean;
  onClose: () => void;
  expense?: Expense | null;
  categories: ExpenseCategory[];
  vendors: Vendor[];
  events: Event[];
  onExpenseSaved: () => void;
}

const PAYMENT_METHODS = [
  { value: "cash", label: "Cash" },
  { value: "card", label: "Card" },
  { value: "online", label: "Online" },
  { value: "bank_transfer", label: "Bank Transfer" },
  { value: "cheque", label: "Cheque" },
];

export default function ExpenseModal({
  isOpen,
  onClose,
  expense,
  categories,
  vendors,
  events,
  onExpenseSaved,
}: ExpenseModalProps) {
  const { showToast } = useToast();
  const [loading, setLoading] = useState(false);
  const [proofFile, setProofFile] = useState<File | null>(null);

  const [formData, setFormData] = useState<Partial<Expense>>({
    title: "",
    description: "",
    amount: 0,
    expense_date: localToday(),
    expense_category_id: undefined,
    vendor_id: undefined,
    event_id: undefined,
    payment_method: "cash",
    reference_number: "",
    notes: "",
  });

  // Populate form when editing
  useEffect(() => {
    if (expense) {
      setFormData({
        ...expense,
        expense_date: expense.expense_date ? formatDateForInput(expense.expense_date) : localToday(),
      });
      setProofFile(null); // Reset proof file when editing
    } else {
      // Reset form for new expense
      setFormData({
        title: "",
        description: "",
        amount: 0,
        expense_date: localToday(),
        expense_category_id: undefined,
        vendor_id: undefined,
        event_id: undefined,
        payment_method: "cash",
        reference_number: "",
        notes: "",
      });
      setProofFile(null);
    }
  }, [expense, isOpen]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!formData.title || !formData.amount || !formData.expense_date) {
      showToast("Please fill in all required fields", "error");
      return;
    }

    setLoading(true);
    try {
      // Build FormData for file upload support
      const formDataToSend = new FormData();
      formDataToSend.append("title", formData.title || "");
      formDataToSend.append("amount", String(formData.amount || 0));
      formDataToSend.append("expense_date", formData.expense_date || "");

      if (formData.description) {
        formDataToSend.append("description", formData.description);
      }
      if (formData.expense_category_id) {
        formDataToSend.append(
          "expense_category_id",
          String(formData.expense_category_id)
        );
      }
      if (formData.vendor_id) {
        formDataToSend.append("vendor_id", String(formData.vendor_id));
      }
      if (formData.event_id) {
        formDataToSend.append("event_id", String(formData.event_id));
      }
      if (formData.payment_method) {
        formDataToSend.append("payment_method", formData.payment_method);
      }
      if (formData.reference_number) {
        formDataToSend.append("reference_number", formData.reference_number);
      }
      if (formData.notes) {
        formDataToSend.append("notes", formData.notes);
      }
      if (proofFile) {
        formDataToSend.append("proof_file", proofFile);
      }

      let response;
      if (expense?.id) {
        // Update existing expense
        response = await apiClient.postFormData(
          `/expenses/${expense.id}?_method=PUT`,
          formDataToSend
        );
      } else {
        // Create new expense
        response = await apiClient.postFormData("/expenses", formDataToSend);
      }

      if (response.success) {
        showToast(
          expense?.id
            ? "Expense updated successfully"
            : "Expense created successfully",
          "success"
        );
        onExpenseSaved();
        onClose();
      } else {
        showToast(response.message || "Failed to save expense", "error");
      }
    } catch (error: any) {
      console.error("Error saving expense:", error);
      showToast(error?.message || "Failed to save expense", "error");
    } finally {
      setLoading(false);
    }
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={expense?.id ? "Edit Expense" : "Add Expense"}
      size="lg"
    >
      <form onSubmit={handleSubmit} className="space-y-4">
        {/* Title */}
        <div>
          <label className="block text-xs font-medium text-stone-600 mb-1.5">
            Title <span className="text-red-500">*</span>
          </label>
          <div className="relative">
            <FileText className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-stone-400" />
            <input
              type="text"
              value={formData.title || ""}
              onChange={(e) =>
                setFormData({ ...formData, title: e.target.value })
              }
              placeholder="Expense title (e.g., Catering Payment)"
              className="w-full pl-9 pr-3 py-2.5 rounded-xl border border-stone-200 text-sm focus:border-amber-400 focus:ring-2 focus:ring-amber-100 outline-none"
              required
            />
          </div>
        </div>

        {/* Amount and Date */}
        <div className="grid grid-cols-2 gap-4">
          <div>
            <label className="block text-xs font-medium text-stone-600 mb-1.5">
              Amount <span className="text-red-500">*</span>
            </label>
            <div className="relative">
              <span className="absolute left-3 top-1/2 -translate-y-1/2 text-stone-400 text-sm font-medium">₹</span>
              <input
                type="number"
                step="0.01"
                min="0"
                value={formData.amount || ""}
                onChange={(e) =>
                  setFormData({ ...formData, amount: parseFloat(e.target.value) })
                }
                placeholder="0.00"
                className="w-full pl-8 pr-3 py-2.5 rounded-xl border border-stone-200 text-sm focus:border-amber-400 focus:ring-2 focus:ring-amber-100 outline-none"
                required
              />
            </div>
          </div>
          <div>
            <label className="block text-xs font-medium text-stone-600 mb-1.5">
              Date <span className="text-red-500">*</span>
            </label>
            <div className="relative">
              <Calendar className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-stone-400" />
              <input
                type="date"
                value={formData.expense_date || ""}
                onChange={(e) =>
                  setFormData({ ...formData, expense_date: e.target.value })
                }
                className="w-full pl-9 pr-3 py-2.5 rounded-xl border border-stone-200 text-sm focus:border-amber-400 focus:ring-2 focus:ring-amber-100 outline-none"
                required
              />
            </div>
          </div>
        </div>

        {/* Category */}
        <div>
          <label className="block text-xs font-medium text-stone-600 mb-1.5">
            <Tag className="inline w-3.5 h-3.5 mr-1" />
            Category
          </label>
          <SearchableDropdown
            options={[
              { value: "", label: "Select Category" },
              ...categories.map((cat) => ({
                value: String(cat.id),
                label: cat.name,
              })),
            ]}
            value={formData.expense_category_id ? String(formData.expense_category_id) : ""}
            onChange={(value) =>
              setFormData({
                ...formData,
                expense_category_id: value ? parseInt(value) : undefined,
              })
            }
            placeholder="Select Category"
          />
        </div>

        {/* Payment Method */}
        <div>
          <label className="block text-xs font-medium text-stone-600 mb-1.5">
            <CreditCard className="inline w-3.5 h-3.5 mr-1" />
            Payment Method
          </label>
          <SearchableDropdown
            options={PAYMENT_METHODS.map((method) => ({
              value: method.value,
              label: method.label,
            }))}
            value={formData.payment_method || "cash"}
            onChange={(value) =>
              setFormData({ ...formData, payment_method: value })
            }
            placeholder="Select Payment Method"
          />
        </div>

        {/* Reference Number */}
        <div>
          <label className="block text-xs font-medium text-stone-600 mb-1.5">
            Reference Number / Transaction ID
          </label>
          <input
            type="text"
            value={formData.reference_number || ""}
            onChange={(e) =>
              setFormData({ ...formData, reference_number: e.target.value })
            }
            placeholder="e.g., TXN123456 or Cheque #001"
            className="w-full px-3 py-2.5 rounded-xl border border-stone-200 text-sm focus:border-amber-400 focus:ring-2 focus:ring-amber-100 outline-none"
          />
        </div>

        {/* Description */}
        <div>
          <label className="block text-xs font-medium text-stone-600 mb-1.5">
            Description
          </label>
          <textarea
            value={formData.description || ""}
            onChange={(e) =>
              setFormData({ ...formData, description: e.target.value })
            }
            placeholder="Additional details about this expense..."
            rows={2}
            className="w-full px-3 py-2.5 rounded-xl border border-stone-200 text-sm focus:border-amber-400 focus:ring-2 focus:ring-amber-100 outline-none resize-none"
          />
        </div>

        {/* Proof Upload */}
        <div>
          <label className="block text-xs font-medium text-stone-600 mb-1.5">
            Receipt / Proof of Payment
            {expense?.proof_path && (
              <span className="text-emerald-600 ml-2">(Already uploaded)</span>
            )}
          </label>
          <ImageUpload
            selectedFile={proofFile}
            onFileSelect={setProofFile}
            maxSize={2 * 1024 * 1024}
            compressToWebP={true}
          />
          {expense?.proof_path && !proofFile && (
            <p className="text-xs text-stone-500 mt-1">
              Current: {" "}
              <a
                href={getImageUrl(expense.proof_path)}
                target="_blank"
                rel="noopener noreferrer"
                className="text-amber-600 hover:underline"
              >
                View existing proof
              </a>
            </p>
          )}
        </div>

        {/* Notes */}
        <div>
          <label className="block text-xs font-medium text-stone-600 mb-1.5">
            Internal Notes
          </label>
          <textarea
            value={formData.notes || ""}
            onChange={(e) =>
              setFormData({ ...formData, notes: e.target.value })
            }
            placeholder="Internal notes (not shown on reports)..."
            rows={2}
            className="w-full px-3 py-2.5 rounded-xl border border-stone-200 text-sm focus:border-amber-400 focus:ring-2 focus:ring-amber-100 outline-none resize-none"
          />
        </div>

        {/* Actions */}
        <div className="flex gap-3 pt-2">
          <button
            type="button"
            onClick={onClose}
            className="flex-1 py-3 px-4 text-sm font-medium border border-stone-300 text-stone-600 rounded-xl hover:bg-stone-50 transition-colors"
          >
            Cancel
          </button>
          <button
            type="submit"
            disabled={loading}
            className="flex-1 py-3 px-4 text-sm font-semibold bg-amber-600 hover:bg-amber-700 text-white rounded-xl disabled:opacity-50 transition-colors flex items-center justify-center gap-2"
          >
            {loading ? (
              <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
            ) : (
              <>{expense?.id ? "Update" : "Create"} Expense</>
            )}
          </button>
        </div>
      </form>
    </Modal>
  );
}
